/**
 * Service Detail data layer used by every tab under `/services/:id`.
 *
 * The custom-fields CRUD endpoints aren't in the generated OpenAPI types
 * yet (regen lands them), so the mutation helpers hand-roll the request
 * via `authFetchJson` — same auth/refresh/bounce contract as `api.GET`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";

export type CustomFieldType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "date"
  | "datetime"
  | "file"
  | "checkbox";

/** Date and Date-and-Time fields share the calendar + single-main-date behaviour. */
export const isDateLike = (t: CustomFieldType | string): boolean =>
  t === "date" || t === "datetime";

/** One row in the Select-options editor. The stored `value` may differ from
 *  the displayed `label` so that operator-renamed labels don't break stored
 *  booking values. */
export type SelectOption = { value: string; label: string };

export type CustomFieldValidation = {
  min?: number;
  max?: number;
  pattern?: string;
  fileTypes?: string[];
  maxFileSizeMb?: number;
};

export type CustomFieldSettings = {
  placeholder?: string;
  helpText?: string;
  defaultValue?: string;
  validation?: CustomFieldValidation;
  /** DATE fields only: the service's single "main date" — its value drives the
   *  operator's Bookings-list Date column. At most one per service (enforced
   *  server-side). Independent of `showOnCalendar`. */
  primaryDate?: boolean;
  /** DATE fields only: place this date on the operator calendar. Any number of
   *  fields per service may set it. Independent of `primaryDate`. */
  showOnCalendar?: boolean;
};

export type CustomField = {
  id: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  /** Server stores either `string[]`, `{values: string[]}`, or the new
   *  `{value, label}[]` shape — use `normalizeOptions` to flatten on read. */
  options?: unknown;
  /** Placeholder / help text / default value / validation. Null when the
   *  field hasn't been opened in the settings panel yet. */
  settings?: CustomFieldSettings | null;
  /** Sort key. Optional only because the OpenAPI types lag the backend
   *  (which always returns it). Treat as defined-or-zero at call sites. */
  orderIndex?: number;
  /** Customer-scope: tag ids the field applies to. Empty = no tag scope. */
  scopeTagIds?: string[];
  /** Customer-scope: specific client ids the field applies to. Empty = none. */
  scopeClientIds?: string[];
  /** Inventory-scope: category names the field applies to. Empty = none. */
  scopeCategories?: string[];
};

/** Normalize the polymorphic `options` blob to the rich `{value, label}[]`
 *  shape the panel + booking flow consume. Accepts string arrays + the
 *  legacy `{values: [...]}` wrapper for back-compat. */
export function normalizeOptions(raw: unknown): SelectOption[] {
  const list: unknown =
    Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object" && Array.isArray((raw as { values?: unknown }).values)
        ? (raw as { values: unknown[] }).values
        : null;
  if (!Array.isArray(list)) return [];
  return list
    .map((node): SelectOption | null => {
      if (typeof node === "string") return { value: node, label: node };
      if (
        node &&
        typeof node === "object" &&
        typeof (node as { value?: unknown }).value === "string"
      ) {
        const o = node as { value: string; label?: unknown };
        return {
          value: o.value,
          label: typeof o.label === "string" && o.label ? o.label : o.value,
        };
      }
      return null;
    })
    .filter((o): o is SelectOption => o !== null);
}

/**
 * Per-booking value for one of the service's custom fields. Returned by
 * `GET /api/v1/bookings/{id}` as `customFieldValues`. Snapshots `label` and
 * `type` at booking time so historical bookings stay readable even after
 * the schema field is renamed or deleted.
 */
export type CustomFieldValue = {
  fieldId: string;
  label: string;
  type: CustomFieldType;
  value: string | null;
};

/**
 * Resolve the booking-flow primary-picker section title for a service.
 * Order of precedence:
 *   1. Operator override on `service.tierLabel` (non-empty trimmed string).
 *   2. If every tier-linked item shares one category → that category name.
 *   3. Fallback "Plan".
 *
 * Both args are loosely typed because the OpenAPI codegen lags the
 * backend on `tierLabel` and the inventory-link `viaCategory` field.
 */
type TierLinkLike = {
  viaCategory?: string | null;
  item?: { category?: string | null } | null;
};

export function deriveTierLabel(
  service: { tierLabel?: string | null } | null | undefined,
  tierLinks: TierLinkLike[],
): string {
  const override = service?.tierLabel?.trim();
  if (override) return override;
  const cats = new Set(
    tierLinks
      .map((l) => l.viaCategory ?? l.item?.category ?? null)
      .filter((c): c is string => !!c && c.trim().length > 0),
  );
  if (cats.size === 1) return [...cats][0]!;
  return "Plan";
}

/** Shared cache for the whole service-detail page. */
export function useServiceDetail(id: string | undefined) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["service", id],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/services/{id}", {
          params: { path: { id: id! } },
        }),
      ),
  });
}

// --- custom field mutations (hand-rolled until codegen lands) --------------

export type CustomFieldInput = {
  label: string;
  type: CustomFieldType;
  required?: boolean;
  options?: SelectOption[] | unknown;
  settings?: CustomFieldSettings | null;
  /** Customer scope (omit for per-service / inventory). */
  scopeTagIds?: string[];
  scopeClientIds?: string[];
  /** Inventory scope (omit for per-service / customer). */
  scopeCategories?: string[];
};

export async function createCustomField(
  serviceId: string,
  body: CustomFieldInput,
): Promise<CustomField> {
  return authFetchJson<CustomField>(`/api/v1/services/${serviceId}/custom-fields`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateCustomField(
  serviceId: string,
  fieldId: string,
  body: Partial<CustomFieldInput>,
): Promise<CustomField> {
  return authFetchJson<CustomField>(
    `/api/v1/services/${serviceId}/custom-fields/${fieldId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteCustomField(
  serviceId: string,
  fieldId: string,
): Promise<void> {
  const res = await authFetch(
    `/api/v1/services/${serviceId}/custom-fields/${fieldId}`,
    { method: "DELETE" },
  );
  await throwIfNotOk(res);
}

export async function reorderCustomFields(
  serviceId: string,
  ids: string[],
): Promise<void> {
  const res = await authFetch(
    `/api/v1/services/${serviceId}/custom-fields/reorder`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    },
  );
  await throwIfNotOk(res);
}

/**
 * Duplicate a service into a new draft. Backed by
 * `POST /api/v1/services/{id}/duplicate`. The copy starts with
 * `active = false` and carries the custom-field schema across;
 * bookings / customers / payments do NOT travel with it.
 */
export function useDuplicateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/v1/services/{id}/duplicate", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
    },
  });
}
